// src/pages/Nom035AnalyticsPage.jsx
import React, { useEffect, useState, useMemo } from "react";
import { useParams, useLocation } from "react-router-dom";
import questionnaireScopeApi from "../../api/questionnaireScopeApi";
import questionnaireApplicationsApi from "../../api/questionnaireApplicationsApi";
import nom35AnalyticsApi from "../../api/nom35AnalyticsApi";
import ageRanges from "../../data/ageRanges";
import biologicalSexList from "../../data/biologicalSex";
import nom035Ranges from "../../data/nom035Ranges";
import Nom035SummaryChart from "../../components/analytics/Nom035SummaryChart";
import Nom035JobAreasChart from "../../components/analytics/Nom035JobAreasChart";
import { generateNom035Report } from "../../utils/Nom035ReportGenerator";
import calculateNom035MinimumRespondents from "../../utils/nom035SampleSize";
import styles from "./Nom035AnalyticsPage.module.css";

const categoryKeys = [
  "environment",
  "factors",
  "timeOrganization",
  "leadershipRelations",
  "organizationalEnvironment"
];
const categoryLabels = {
  environment: "Ambiente de trabajo",
  factors: "Factores de la actividad",
  timeOrganization: "Organización del tiempo",
  leadershipRelations: "Liderazgo y relaciones",
  organizationalEnvironment: "Entorno organizacional"
};

const domainLabels = {
  conditions: "Condiciones ambientales",
  workload: "Carga de trabajo",
  control: "Falta de control",
  workday: "Jornada de trabajo",
  interference: "Interferencia en el trabajo",
  leadership: "Liderazgo",
  relationships: "Relaciones en el trabajo",
  violence: "Violencia",
  recognition: "Reconocimiento",
  belonging: "Sentido de pertenencia"
};

const levelOrder = ["Nulo", "Bajo", "Medio", "Alto", "Muy alto"];
const globalRangeLabels = levelOrder.reduce((acc, lvl, i) => {
  acc[lvl] = nom035Ranges.global.range[i];
  return acc;
}, {});

const categoryRangeLabels = {};
const domainRangeLabels = {};
Object.entries(nom035Ranges.categoryDomains).forEach(
  ([catKey, { range, domains }]) => {
    categoryRangeLabels[catKey] = levelOrder.reduce((acc, lvl, i) => {
      acc[lvl] = range[i];
      return acc;
    }, {});
    Object.entries(domains).forEach(([domKey, { range: domRange }]) => {
      domainRangeLabels[domKey] = levelOrder.reduce((acc, lvl, i) => {
        acc[lvl] = domRange[i];
        return acc;
      }, {});
    });
  }
);

export default function Nom035AnalyticsPage({
  companyId: companyIdProp,
  applicationId: applicationIdProp,
}) {
  const {
    companyId: paramCompanyId,
    branchId,
    applicationId: paramApplicationId,
  } = useParams();
  const location = useLocation();
  const { state } = location;

  const companyId = companyIdProp || paramCompanyId || state?.companyId;
  const applicationId = applicationIdProp || paramApplicationId;
  const applicationIds = useMemo(() => {
    if (applicationId) return [];
    return [...new Set(
      new URLSearchParams(location.search)
        .getAll("applicationIds")
        .flatMap(value => value.split(","))
        .map(value => value.trim())
        .filter(Boolean)
    )];
  }, [applicationId, location.search]);
  const applicationIdsKey = applicationIds.join(",");
  const applicationScopeFilters = useMemo(() => (
    applicationId
      ? { applicationId }
      : applicationIdsKey
        ? { applicationIds: applicationIdsKey }
        : {}
  ), [applicationId, applicationIdsKey]);
  const isApplicationFiltered = Boolean(applicationId || applicationIdsKey);
  const isGroupedView = applicationIds.length > 1;
  const analyticsScopeId = branchId || companyId;
  const areaScopeId = analyticsScopeId;
  const [companyJobAreas, setCompanyJobAreas] = useState([]);
  const [usesEmployeeCredentials, setUsesEmployeeCredentials] = useState(false);
  const [companyData, setCompanyData] = useState({ name: "", employeesNumber: 0 });

  // Respondents (empleados)
  const [respondents, setRespondents] = useState([]);
  const [empSortBy, setEmpSortBy] = useState("email");
  const [empSortOrder, setEmpSortOrder] = useState("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 15;
  const [downloading, setDownloading] = useState(false); // ⬅️ para Excel

  // Totales y PDF
  const [totalResponses, setTotalResponses] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [filters, setFilters] = useState({ ageRange: "", jobArea: "", biologicalSex: "" });

  useEffect(() => {
    setFilters({ ageRange: "", jobArea: "", biologicalSex: "" });
  }, [analyticsScopeId, applicationId, applicationIdsKey]);

  useEffect(() => {
    if (!analyticsScopeId) return;
    let active = true;
    setTotalResponses(null);
    nom35AnalyticsApi.getResponsesCountByCompanyId(analyticsScopeId, {
      ...filters, ...applicationScopeFilters,
    }).then(result => { if (active) setTotalResponses(result.totalResponses); })
      .catch(() => { if (active) setTotalResponses(null); });
    return () => { active = false; };
  }, [analyticsScopeId, applicationId, applicationIdsKey, applicationScopeFilters, filters]);

  useEffect(() => {
    if (!analyticsScopeId || !areaScopeId) return;
    let cancelled = false;
    setCurrentPage(1);
    setRespondents([]);
    setCompanyJobAreas([]);
    (async () => {
      try {
        const comp = await questionnaireScopeApi.getById(areaScopeId);
        if (cancelled) return;
        setCompanyData({
          name: comp.name,
          employeesNumber: 0
        });
      } catch (err) {
        console.error("Error cargando empresa/sucursal:", err);
      }
      try {
        if (applicationId) {
          const config = await questionnaireApplicationsApi.getNom035Config(applicationId);
          if (cancelled) return;
          setCompanyJobAreas(config.jobAreas || []);
          setUsesEmployeeCredentials(Boolean(config.usesEmployeeCredentials));
          setEmpSortBy(config.usesEmployeeCredentials ? "employee_number" : "email");
        } else {
          const config = isApplicationFiltered
            ? await nom35AnalyticsApi.getScopeConfig(analyticsScopeId, applicationScopeFilters)
            : await nom35AnalyticsApi.getScopeConfig(analyticsScopeId);
          if (cancelled) return;
          setCompanyJobAreas(config.jobAreas || []);
          setUsesEmployeeCredentials(false);
          setEmpSortBy("email");
        }
      } catch (err) {
        if (cancelled) return;
        setCompanyJobAreas([]);
        setUsesEmployeeCredentials(false);
        console.error("Error cargando áreas de la aplicación NOM-035:", err);
      }
      // Respondents
      try {
        const { respondants, totalEmployees } =
          await nom35AnalyticsApi.getRespondantsByCompanyId(
            areaScopeId,
            isApplicationFiltered ? applicationScopeFilters : undefined
          );
        if (cancelled) return;
        setRespondents(respondants);
        setCompanyData((current) => ({
          ...current,
          employeesNumber: totalEmployees,
        }));
      } catch (err) {
        console.error("Error obteniendo listado de empleados:", err);
      }
    })();
    return () => { cancelled = true; };
  }, [analyticsScopeId, areaScopeId, applicationId, applicationIdsKey, applicationScopeFilters, isApplicationFiltered]);

  const jobAreaOptions = companyJobAreas;

  const filterConfigs = [
    { name: "ageRange", label: "Rango de edad", options: ageRanges },
    { name: "jobArea", label: "Área de trabajo", options: jobAreaOptions },
    { name: "biologicalSex", label: "Sexo biológico", options: biologicalSexList }
  ];

  const handleDownload = async () => {
    setGenerating(true);
    try {
      await generateNom035Report(
        analyticsScopeId,
        { ...filters, ...applicationScopeFilters },
        areaScopeId
      );
    } catch (err) {
      console.error("Error generando reporte PDF:", err);
    } finally {
      setGenerating(false);
    }
  };

  // Excel respondants (NOM-035)
  const handleDownloadRespondentsExcel = async () => {
    if (!areaScopeId) return;
    try {
      setDownloading(true);
      await nom35AnalyticsApi.downloadRespondantsByCompanyIdExcel(
        areaScopeId,
        applicationScopeFilters
      );
    } catch (err) {
      console.error("Error descargando Excel NOM-035:", err);
    } finally {
      setDownloading(false);
    }
  };

  // Número mínimo de encuestados
  const N = companyData.employeesNumber;
  const minimumRespondents = calculateNom035MinimumRespondents(N);

  // Orden y paginación
  const sortedRespondents = useMemo(() => {
    return [...respondents].sort((a, b) => {
      const field = empSortBy;
      if (a[field] < b[field]) return empSortOrder === "asc" ? -1 : 1;
      if (a[field] > b[field]) return empSortOrder === "asc" ? 1 : -1;
      return 0;
    });
  }, [respondents, empSortBy, empSortOrder]);

  const totalPages = Math.ceil(sortedRespondents.length / pageSize) || 1;
  const displayedRespondents = sortedRespondents.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const onSort = (column) => {
    if (empSortBy === column) {
      setEmpSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setEmpSortBy(column);
      setEmpSortOrder("asc");
    }
  };

  const SortArrow = ({ column }) => {
    const isActive = empSortBy === column;
    if (!isActive) {
      return (
        <span className={styles.sortArrow}>
          <svg width="8" height="8" viewBox="0 0 8 8">
            <polygon points="4,0 8,8 0,8" fill="currentColor" />
          </svg>
          <svg width="8" height="8" viewBox="0 0 8 8">
            <polygon points="0,0 8,0 4,8" fill="currentColor" />
          </svg>
        </span>
      );
    }
    return (
      <span className={styles.sortArrow}>
        {empSortOrder === "asc" ? (
          <svg width="8" height="8" viewBox="0 0 8 8">
            <polygon points="4,0 8,8 0,8" fill="currentColor" />
          </svg>
        ) : (
          <svg width="8" height="8" viewBox="0 0 8 8">
            <polygon points="0,0 8,0 4,8" fill="currentColor" />
          </svg>
        )}
      </span>
    );
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <span className={styles.eyebrow}>ANALÍTICA DE BIENESTAR</span>
          <h2 className={styles.title}>NOM-035</h2>
          <p className={styles.subheader}>
            {isGroupedView
              ? `Resultados agrupados de ${applicationIds.length} aplicaciones de `
              : isApplicationFiltered
                ? "Resultados de "
                : "Resultados de todas las aplicaciones de "}
            <span className={styles.companyName}>{companyData.name || "la empresa"}</span>
          </p>
        </div>
        <button className={styles.downloadButton} onClick={handleDownload} disabled={generating}>
          {generating ? "Generando reporte…" : "Descargar reporte PDF"}
        </button>
      </header>

      {totalResponses !== null && (
        <div className={styles.summaryCards}>
          <div className={`${styles.summaryCard} ${styles.summaryCardAccent}`}>
            <span>{isApplicationFiltered ? "Encuestados con estos filtros" : "Respuestas acumuladas"}</span>
            <strong>{totalResponses}</strong>
          </div>
          <div className={styles.summaryCard}>
            <span>{isApplicationFiltered ? "Total de empleados" : "Registros en aplicaciones"}</span>
            <strong>{companyData.employeesNumber}</strong>
          </div>
          {applicationId && <div className={styles.summaryCard}>
            <span>Mínimo requerido</span>
            <strong>{minimumRespondents}</strong>
          </div>}
        </div>
      )}

      {!applicationId && <p className={styles.subheader}>
        {isGroupedView
          ? "El resultado agrupa únicamente las aplicaciones seleccionadas. Una persona puede aparecer más de una vez."
          : "El consolidado suma las participaciones de todas las aplicaciones. Una persona puede aparecer más de una vez."}
      </p>}

      <div className="filters" aria-label="Filtros del reporte NOM-035">
        {filterConfigs.map(({ name, label, options }) => (
          <div className="filter-item" key={name}>
            <label htmlFor={`nom035-filter-${name}`}>{label}</label>
            <select id={`nom035-filter-${name}`} value={filters[name]}
              onChange={event => setFilters(current => ({ ...current, [name]: event.target.value }))}>
              <option value="">Todos</option>
              {options.map(option => <option key={option} value={option}>{option}</option>)}
            </select>
          </div>
        ))}
      </div>

      {/* Cuestionario 1 */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>
          Cuestionario 1 - Acontecimientos Traumáticos Severos
        </h3>
        <div className={styles.twoCols}>
          <Nom035SummaryChart
            companyId={analyticsScopeId}
            applicationId={applicationId}
            applicationIds={applicationIds}
            clinical
            title="Acontecimientos Traumáticos Severos"
            filters={filters}
          />
        </div>
      </section>

      {/* Resumen general */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Resumen general</h3>

        <div className={styles.fullWidth}>
          <Nom035SummaryChart
            companyId={analyticsScopeId}
            applicationId={applicationId}
            applicationIds={applicationIds}
            metric="global"
            title="Riesgo global"
            filters={filters}
            rangeLabels={globalRangeLabels}
          />
          <Nom035JobAreasChart
            companyId={areaScopeId}
            applicationId={applicationId}
            applicationIds={applicationIds}
            metric="global"
            title="Puntaje por área de trabajo (Global)"
            filters={filters}
          />
        </div>
      </section>

      <hr className={styles.separator} />

      {/* Por categoría y sus dominios */}
      {categoryKeys.map((catKey, idx) => {
        const domains = Object.keys(
          nom035Ranges.categoryDomains[catKey].domains
        );
        return (
          <React.Fragment key={catKey}>
            <section className={styles.section}>
              <h3 className={styles.sectionTitle}>
                {categoryLabels[catKey]}
              </h3>

              <div className={styles.twoCols}>
                <Nom035SummaryChart
                  companyId={analyticsScopeId}
                  applicationId={applicationId}
                  applicationIds={applicationIds}
                  metric={catKey}
                  title="General"
                  filters={filters}
                  rangeLabels={categoryRangeLabels[catKey]}
                />
                {domains.map((domKey) => (
                  <Nom035SummaryChart
                    key={domKey}
                    companyId={analyticsScopeId}
                    applicationId={applicationId}
                    applicationIds={applicationIds}
                    metric={domKey}
                    title={domainLabels[domKey]}
                    filters={filters}
                    rangeLabels={domainRangeLabels[domKey]}
                  />
                ))}
              </div>

              <div className={styles.fullWidth}>
                <Nom035JobAreasChart
                  companyId={areaScopeId}
                  applicationId={applicationId}
                  applicationIds={applicationIds}
                  metric={catKey}
                  title={`Puntaje por área de trabajo: ${categoryLabels[catKey]}`}
                  filters={filters}
                />
              </div>
            </section>
            {idx < categoryKeys.length - 1 && <hr className={styles.separator} />}
          </React.Fragment>
        );
      })}

      {/* Listado de Empleados */}
      <hr className={styles.separator} />
      <section className={`${styles.section} ${styles.responsesSection}`}>
        <div className={styles.tableHeader}>
          <div>
            <span className={styles.eyebrow}>DETALLE</span>
            <h3 className={styles.sectionTitle}>Listado de empleados</h3>
            <p>{respondents.length} {isApplicationFiltered ? "empleados registrados" : "registros en aplicaciones"}</p>
          </div>
          <button
            onClick={handleDownloadRespondentsExcel}
            disabled={downloading || !analyticsScopeId}
            className={styles.exportButton}
            title="Exportar listado de empleados (NOM-035) a Excel"
          >
            {downloading ? "Descargando…" : "Exportar Excel"}
          </button>
        </div>
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>#</th>
                {usesEmployeeCredentials ? (
                  <>
                    <th className={styles.clickable} onClick={() => onSort("employee_number")}>
                      Número de empleado <SortArrow column="employee_number" />
                    </th>
                    <th className={styles.clickable} onClick={() => onSort("employee_code")}>
                      Código de empleado <SortArrow column="employee_code" />
                    </th>
                  </>
                ) : (
                  <th className={styles.clickable} onClick={() => onSort("email")}>
                    {isApplicationFiltered && !isGroupedView ? "Correo" : "Correo / número de empleado"} <SortArrow column="email" />
                  </th>
                )}
                <th className={styles.clickable} onClick={() => onSort("status")}>
                  Estado <SortArrow column="status" />
                </th>
              </tr>
            </thead>
            <tbody>
              {displayedRespondents.map((r, i) => (
                <tr key={`${r.email || r.employee_number}-${i}`}>
                  <td>{(currentPage - 1) * pageSize + i + 1}</td>
                  {usesEmployeeCredentials ? (
                    <>
                      <td>{r.employee_number}</td>
                      <td>{r.employee_code}</td>
                    </>
                  ) : <td>{r.email || `${r.employee_number || "—"} / ${r.employee_code || "—"}`}</td>}
                  <td>{r.status}</td>
                </tr>
              ))}
              {displayedRespondents.length === 0 && (
                <tr>
                  <td colSpan={usesEmployeeCredentials ? 4 : 3} className={styles.message}>
                    No hay empleados para mostrar.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Paginación + botón Excel */}
        <div className={styles.pagination}>
          <button
            onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
            disabled={currentPage === 1}
            className={styles.paginationButton}
          >
            Anterior
          </button>
          <span className={styles.pageInfo}>
            Página {currentPage} de {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
            disabled={currentPage === totalPages}
            className={styles.paginationButton}
          >
            Siguiente
          </button>
        </div>
      </section>
    </div>
  );
}
